import React, { useState, useContext, useEffect } from 'react';
import { Box, Button, Drawer, Typography, TextField, InputAdornment } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { putNewUserContactInfo } from '../../../lib/api/putNewUserContactInfo';
import { putNewUserContactInfoOTP } from '../../../lib/api/putNewUserContactInfoOTP';
import { UserContext } from '../../../lib/contexts/UserContext';
import { enqueueSnackbar } from 'notistack';

const InfoUpdateCard = ({ editedInfo = 'unknown' }) => {
  const {
    actions: { refetch },
  } = useContext(UserContext);
  const [isOpen, setIsOpen] = useState(false);
  const [buttonText, setButtonText] = useState('Send Verification Code');
  const [infoInputValue, setInfoInputValue] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [sentInfoInputValue, setSentInfoInputValue] = useState(false);
  const [serviceID, setServiceID] = useState('');

  const validateEmail = (email) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const validatePhone = (phone) => {
    return /^\(\d{3}\)-\d{3}-\d{4}$/.test(phone);
  };

  const [emailError, setEmailError] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState('Invalid email address');
  const [phoneError, setPhoneError] = useState(false);
  const [phoneErrorText, setPhoneErrorText] = useState('Invalid phone number');
  const [otpError, setOtpError] = useState(false);
  const [otpErrorText, setOtpErrorText] = useState('Invalid OTP');

  const resetState = () => {
    setButtonText('Send Verification Code');
    setInfoInputValue('');
    setVerificationCode('');
    setSentInfoInputValue(false);
    setServiceID('');
    setEmailError(false);
    setPhoneError(false);
    setOtpError(false);
    setEmailErrorText('Invalid email address');
    setPhoneErrorText('Invalid phone number');
    setOtpErrorText('Invalid OTP');
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setIsOpen(open);

    if (!open) {
      resetState();
    }
  };

  const reducePhoneNumber = (phoneNumber) => {
    return phoneNumber.replace(/[^\d]/g, '');
  };

  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)})-${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)})-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handlePhoneInputChange = (e) => {
    const formattedPhoneNumber = formatPhoneNumber(e.target.value);
    setInfoInputValue(formattedPhoneNumber);
  };

  const handleClick = async () => {
    if (editedInfo === 'email') {
      if (!validateEmail(infoInputValue)) {
        setEmailError(true);
        return;
      } else {
        setEmailError(false);
      }
    } else if (editedInfo === 'phone') {
      if (!validatePhone(infoInputValue)) {
        setPhoneError(true);
        return;
      } else {
        setPhoneError(false);
      }
    }

    if (sentInfoInputValue) {
      const response = await putNewUserContactInfoOTP(verificationCode, serviceID);
      if (response && response.message === 'Contact info successfully updated.') {
        refetch();
        enqueueSnackbar('Contact info updated successfully!', { variant: 'success' });
        setIsOpen(false);
        resetState();
      } else {
        console.error('Failed to update contact info. Here is the response: ', response);
        setOtpError(true);
      }
    } else {
      const contactInfo = editedInfo === 'phone' ? '+1' + reducePhoneNumber(infoInputValue) : infoInputValue;
      const response = await putNewUserContactInfo(editedInfo, contactInfo);
      if (response?.statusCode === 422) {
        if (editedInfo === 'email') {
          setEmailError(true);
          setEmailErrorText('Email already exists');
        } else if (editedInfo === 'phone') {
          setPhoneError(true);
          setPhoneErrorText('Phone number already exists');
        }
      }
      if (response && response.message === 'Verification code sent.') {
        setServiceID(response.service_id);
        setButtonText('Verify and Save');
        setSentInfoInputValue(true);
        if (editedInfo === 'email') {
          setEmailError(false);
        } else if (editedInfo === 'phone') {
          setPhoneError(false);
        }
      } else {
        console.error('Failed to update contact info. Here is the response: ', response);
        if (editedInfo === 'email') {
          setEmailError(true);
        } else if (editedInfo === 'phone') {
          setPhoneError(true);
        }
      }
    }
  };

  return (
    <Box>
      <Button onClick={toggleDrawer(true)}>
        <EditIcon />
      </Button>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={toggleDrawer(false)}
      >
        <Box
          id="info-update-card-container"
          sx={{
            display: 'flex',
            width: '440px',
            height: '100%',
            padding: '40px 32px',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            borderLeft: '1px solid #EBE8F3',
            background: '#FDFDFD',
            boxShadow:
              '0px 4px 5px 0px rgba(0, 0, 0, 0.02), 0px 1px 10px 0px rgba(0, 0, 0, 0.02), 0px 2px 4px -1px rgba(17, 17, 17, 0.03)',
          }}
          role="presentation"
          //   onClick={toggleDrawer(false)}
          //   onKeyDown={toggleDrawer(false)}
        >
          <Box
            id="info-update-card-main-container"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '40px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="header-container"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                gap: '10px',
              }}
            >
              <Box
                id="inner-header-container"
                sx={{
                  display: 'flex',
                  width: '376px',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <Box
                  id="back-button-container"
                  sx={{
                    display: 'flex',
                    width: '24px',
                    height: '38px',
                    alignItems: 'center',
                    gap: '10px',
                    flexShrink: '0',
                  }}
                >
                  <ArrowBackIcon
                    onClick={toggleDrawer(false)}
                    sx={{
                      height: '24px',
                      flex: '1 0 0',
                      cursor: 'pointer',
                    }}
                  />
                </Box>
                <Box
                  id="header-text-container"
                  sx={{
                    display: 'flex',
                    width: '309px',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '4px',
                    flexShrink: '0',
                  }}
                >
                  <Typography
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      color: 'rgba(0, 0, 0, 0.87)',
                      fontFeatureSettings: 'liga off, clig off',
                      fontFamily: 'Montserrat',
                      fontSize: '24px',
                      fontStyle: 'normal',
                      fontWeight: '700',
                      lineHeight: '125%',
                    }}
                  >
                    {`Edit ${editedInfo}`}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box
              id="info-update-card-content-container"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '24px',
                alignSelf: 'stretch',
              }}
            >
              <TextField
                id="info-input-textfield"
                label={editedInfo}
                variant="outlined"
                fullWidth
                value={infoInputValue}
                onChange={editedInfo === 'phone' ? handlePhoneInputChange : (e) => setInfoInputValue(e.target.value)}
                error={editedInfo === 'email' ? emailError : phoneError}
                helperText={
                  (editedInfo === 'email' && emailError && emailErrorText) ||
                  (editedInfo === 'phone' && phoneError && phoneErrorText)
                }
                InputProps={{
                  startAdornment:
                    editedInfo === 'phone' ? (
                      <InputAdornment
                        position="start"
                        style={{ color: 'grey' }}
                      >
                        +1
                      </InputAdornment>
                    ) : null,
                  display: 'flex',
                  padding: '0px 12px',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  alignSelf: 'stretch',
                  borderRadius: 'var(--borderRadius, 8px)',
                  border: '1px solid var(--_components-input-outlined-enabledBorder, rgba(0, 0, 0, 0.23))',
                  style: {
                    flex: '1 0 0',
                    color: 'var(--text-primary, rgba(0, 0, 0, 0.87))',
                    fontFeatureSettings: "'liga' off, 'clig' off",
                    fontFamily: 'Montserrat',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: '400',
                    lineHeight: '24px',
                    letterSpacing: '0.15px',
                  },
                }}
                InputLabelProps={{
                  style: {
                    color: 'grey',
                  },
                }}
              />
              {sentInfoInputValue && (
                <TextField
                  label="Verification Code"
                  variant="outlined"
                  fullWidth
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  error={otpError}
                  helperText={otpError && otpErrorText}
                  inputProps={{
                    display: 'flex',
                    padding: '0px 12px',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    alignSelf: 'stretch',
                    borderRadius: 'var(--borderRadius, 8px)',
                    border: '1px solid var(--_components-input-outlined-enabledBorder, rgba(0, 0, 0, 0.23))',
                    style: {
                      flex: '1 0 0',
                      color: 'var(--text-primary, rgba(0, 0, 0, 0.87))',
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontFamily: 'Montserrat',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: '400',
                      lineHeight: '24px',
                      letterSpacing: '0.15px',
                    },
                  }}
                  InputLabelProps={{
                    style: {
                      color: 'grey',
                    },
                  }}
                />
              )}
            </Box>
          </Box>
          <Button
            sx={{
              display: 'flex',
              width: '300px',
              padding: '8px 22px',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '8px',
              background: '#211551',
            }}
            onClick={handleClick}
            disabled={
              buttonText === 'Verify and Save' &&
              !(
                (editedInfo === 'email' && !emailError && infoInputValue !== '') ||
                (editedInfo === 'phone' && !phoneError)
              )
            }
          >
            <Box
              id="button-text-container"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--primary-contrast, #FFF)',
                  fontFeatureSettings: "'liga' off, 'clig' off",
                  fontFamily: 'Montserrat',
                  fontSize: '15px',
                  fontStyle: 'normal',
                  fontWeight: '700',
                  lineHeight: '26px',
                  letterSpacing: '0.46px',
                  textTransform: 'uppercase',
                }}
              >
                {buttonText}
              </Typography>
            </Box>
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
};

export default InfoUpdateCard;
